import React, { useEffect, useState, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { formatEventWhen } from "../hooks/useDbEvents";

const emptyForm = {
  title: "",
  summary: "",
  body: "",
  starts_at: "",
  ends_at: "",
  location: "",
  image_url: "",
  visibility: "public",
  published: true,
};

export default function AdminEvents() {
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [inviteForId, setInviteForId] = useState(null);
  const [pick, setPick] = useState({});
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!supabase) return;
    const { data: ev } = await supabase
      .from("events")
      .select("*")
      .order("starts_at", { ascending: true });
    setList(ev || []);
    const { data: m } = await supabase
      .from("profiles")
      .select("id, email, full_name")
      .order("email");
    setMembers(m || []);
  }, []);

  useEffect(() => {
    if (isSupabaseConfigured) load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!supabase) return;
    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      body: form.body.trim() || null,
      starts_at: form.starts_at
        ? new Date(form.starts_at).toISOString()
        : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      location: form.location.trim() || null,
      image_url: form.image_url.trim() || null,
      visibility: form.visibility,
      published: form.published,
      updated_at: new Date().toISOString(),
    };
    if (!payload.title || !payload.starts_at) {
      setMsg({ type: "danger", text: "Title and start date/time are required." });
      return;
    }
    if (editingId) {
      const { error } = await supabase
        .from("events")
        .update(payload)
        .eq("id", editingId);
      if (error) {
        setMsg({ type: "danger", text: error.message });
        return;
      }
    } else {
      const { error } = await supabase.from("events").insert([payload]);
      if (error) {
        setMsg({ type: "danger", text: error.message });
        return;
      }
    }
    setForm(emptyForm);
    setEditingId(null);
    load();
    setMsg({ type: "success", text: "Event saved." });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this event and its invitations?")) return;
    if (!supabase) return;
    await supabase.from("events").delete().eq("id", id);
    load();
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      summary: ev.summary || "",
      body: ev.body || "",
      starts_at: ev.starts_at ? ev.starts_at.slice(0, 16) : "",
      ends_at: ev.ends_at ? ev.ends_at.slice(0, 16) : "",
      location: ev.location || "",
      image_url: ev.image_url || "",
      visibility: ev.visibility || "public",
      published: !!ev.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openInvites = async (eventId) => {
    setInviteForId(eventId);
    setPick({});
    if (!supabase) return;
    const { data } = await supabase
      .from("event_invitations")
      .select("user_id")
      .eq("event_id", eventId);
    const next = {};
    (data || []).forEach((r) => {
      next[r.user_id] = true;
    });
    setPick(next);
  };

  const saveInvites = async () => {
    if (!supabase || !inviteForId) return;
    setMsg(null);
    const userIds = Object.entries(pick)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const rows = userIds.map((user_id) => ({
      event_id: inviteForId,
      user_id,
      status: "invited",
    }));
    const { error } = await supabase
      .from("event_invitations")
      .upsert(rows, { onConflict: "event_id,user_id" });
    if (error) {
      setMsg({ type: "danger", text: error.message });
      return;
    }
    setMsg({
      type: "success",
      text: `Saved ${rows.length} invitation(s). Members see these on their dashboard (email delivery needs SMTP / Edge Functions).`,
    });
    setInviteForId(null);
  };

  const inviteAllMembers = async () => {
    if (!inviteForId || !members.length) return;
    const next = { ...pick };
    members.forEach((m) => {
      next[m.id] = true;
    });
    setPick(next);
  };

  if (!isSupabaseConfigured) {
    return <p className="text-danger">Supabase is not configured.</p>;
  }

  return (
    <div>
      <h1 className="h3 mb-3">Events &amp; invitations</h1>
      <p className="text-muted small">
        <strong>Public</strong> — listed on the website. <strong>Members only</strong> — any
        logged-in member sees it. <strong>Invite only</strong> — only invited people see it on
        their member dashboard.
      </p>

      {msg && (
        <div className={`alert alert-${msg.type === "success" ? "success" : "danger"} small`}>
          {msg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h2 className="h5">{editingId ? "Edit event" : "New event"}</h2>
          <form onSubmit={onSave} className="row g-2">
            <div className="col-md-6">
              <label className="form-label small">Title</label>
              <input
                className="form-control form-control-sm"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Visibility</label>
              <select
                className="form-select form-select-sm"
                value={form.visibility}
                onChange={(e) => setForm({ ...form, visibility: e.target.value })}
              >
                <option value="public">Public (website + members)</option>
                <option value="members_only">Members only (login required)</option>
                <option value="invite_only">Invite only (invited members)</option>
              </select>
            </div>
            <div className="col-12">
              <label className="form-label small">Summary (short)</label>
              <input
                className="form-control form-control-sm"
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
              />
            </div>
            <div className="col-12">
              <label className="form-label small">Full description</label>
              <textarea
                className="form-control form-control-sm"
                rows={4}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Starts</label>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Ends (optional)</label>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Published</label>
              <select
                className="form-select form-select-sm"
                value={form.published ? "yes" : "no"}
                onChange={(e) =>
                  setForm({ ...form, published: e.target.value === "yes" })
                }
              >
                <option value="yes">Yes</option>
                <option value="no">No (draft)</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small">Location</label>
              <input
                className="form-control form-control-sm"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Image URL (optional)</label>
              <input
                className="form-control form-control-sm"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn_theme btn_sm">
                {editingId ? "Update" : "Create"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                  }}
                >
                  Cancel edit
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <h2 className="h5">All events</h2>
      <div className="table-responsive bg-white rounded shadow-sm">
        <table className="table table-sm align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>When</th>
              <th>Title</th>
              <th>Visibility</th>
              <th>Pub</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted small">
                  No rows yet — run <code>supabase/events_and_invitations.sql</code> if queries fail.
                </td>
              </tr>
            )}
            {list.map((ev) => (
              <tr key={ev.id}>
                <td className="small">{formatEventWhen(ev.starts_at, ev.ends_at)}</td>
                <td>{ev.title}</td>
                <td>
                  <code>{ev.visibility}</code>
                </td>
                <td>{ev.published ? "✓" : "—"}</td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn btn-link btn-sm py-0"
                    onClick={() => startEdit(ev)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm py-0"
                    onClick={() => openInvites(ev.id)}
                  >
                    Invites
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm py-0 text-danger"
                    onClick={() => onDelete(ev.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {inviteForId && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,.35)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invite members</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setInviteForId(null)}
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Selected members will see this event if it is <strong>invite only</strong>, and
                  will always see it under “Invitations” on their dashboard.
                </p>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mb-2"
                  onClick={inviteAllMembers}
                >
                  Select all members
                </button>
                <div style={{ maxHeight: 320, overflow: "auto" }} className="border rounded p-2">
                  {members.map((m) => (
                    <label key={m.id} className="d-block small mb-1">
                      <input
                        type="checkbox"
                        className="me-2"
                        checked={!!pick[m.id]}
                        onChange={(e) =>
                          setPick({ ...pick, [m.id]: e.target.checked })
                        }
                      />
                      {m.email}{" "}
                      {m.full_name ? <span className="text-muted">({m.full_name})</span> : null}
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setInviteForId(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn_theme btn_sm"
                  onClick={saveInvites}
                >
                  Save invitations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
